import { jsx as _jsx } from "react/jsx-runtime";
import { IconPathRenderer } from '#pplx/frontend/aether/primitives/IconPrimitive/IconPathRenderer.js';
import { getDefaultStrokeWidth } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconSprite } from '#pplx/frontend/aether/primitives/IconPrimitive/IconSprite.js';
import { IconSymbolRenderer } from '#pplx/frontend/aether/primitives/IconPrimitive/IconSymbolRenderer.js';
import classNames from 'classnames';
import React from 'react';
/**
 * Low-level primitive icon renderer for sprite, path, symbol, and component strategies with
 * numeric or inherit sizing; app code should prefer Icon.
 *
 * @category primitives
 * @keywords icon, primitive, primitives, iconprimitive, component, sprite, path, symbol,
 *   icons-as-data, svg, renderer, numeric, inherit, type, size, strokewidth, aria-label,
 *   aria-hidden, title, description, low-level, accessory
 * @whenToUse IconPrimitive is a low-level building block — use it only as a last resort when the
 *   core Icon component cannot support your use case. Icon provides a unified size API and handles
 *   Tabler icons, sprite-based icons, and fetched icons-as-data symbol definitions automatically.
 *   Reach for IconPrimitive only when building a new Aether core component that needs direct
 *   control over icon rendering internals. In all other cases, use Icon.
 * @guidelineDo
 *   - Use IconPrimitive only when building a new Aether core component that needs direct control
 *     over icon rendering internals
 *   - Use IconPrimitive when you need the raw icon rendering without the size abstraction layer
 *     that Icon provides
 * @guidelineDont
 *   - Don't use IconPrimitive for rendering icons in application code — always use the core Icon
 *     component, which provides a unified size API (tiny, small, medium, default, large, xlarge) and
 *     handles both Tabler icon components and sprite-based icons. IconPrimitive is a last resort
 *     for one-off custom implementations
 *   - Don't reach for IconPrimitive just because you want a different icon size — Icon's size prop
 *     covers all standard sizes and maps to consistent design tokens
 *   - Don't use IconPrimitive to work around Icon's API — if Icon doesn't support your use case,
 *     that's a signal to extend Icon rather than bypass it
 * @accessibility
 *   - IconPrimitive renders the raw SVG icon without any wrapper or size normalization — ensure
 *     the parent element handles sizing and ARIA attributes
 *   - When used inside interactive elements, the icon inherits accessibility from the parent — no
 *     additional ARIA attributes are needed on the primitive itself
 * @relatedComponents
 *   - icon
 *   - accessory
 */
export const IconPrimitive = React.memo(function IconPrimitive({ type, icon, size = 20, strokeWidth: strokeWidthProp, className, style, 'aria-label': ariaLabel, 'aria-hidden': ariaHidden, title, description, }) {
    const strokeWidth = strokeWidthProp ?? getDefaultStrokeWidth(size);
    // Runtime safeguard for when an icon is not found in a registry so
    // lookup returns undefined but it may be casted `as` IconSpriteDef.
    if (!icon) {
        return null;
    }
    if (type === 'path') {
        return (_jsx(IconPathRenderer, { paths: icon, size: size, strokeWidth: strokeWidth, className: className, style: style, "aria-label": ariaLabel, "aria-hidden": ariaHidden, title: title, description: description }));
    }
    if (type === 'symbol') {
        return (_jsx(IconSymbolRenderer, { icon: icon, size: size, strokeWidth: strokeWidth, className: className, style: style, "aria-label": ariaLabel, "aria-hidden": ariaHidden, title: title, description: description }));
    }
    if (type === 'component') {
        const IconComponent = icon;
        return (_jsx(IconComponent, { size: size === 'inherit' ? '1em' : size, strokeWidth: strokeWidth, className: classNames('inline-flex shrink-0', className), style: style, "aria-label": ariaLabel, "aria-hidden": ariaHidden, title: title }));
    }
    // Sprite the implicit default type
    return (_jsx(IconSprite, { name: icon, size: size, strokeWidth: strokeWidth, className: className, style: style, "aria-label": ariaLabel, "aria-hidden": ariaHidden, title: title, description: description }));
});
//# sourceMappingURL=index.js.map